import * as React from 'react';

import { cn } from '../../lib/utils';

export type CardProps<TElement extends React.ElementType = 'div'> =
  React.ComponentPropsWithoutRef<TElement> & {
    as?: TElement;
  };

function Card<TElement extends React.ElementType = 'div'>({
  as,
  className,
  ...props
}: CardProps<TElement>) {
  const Component = (as ?? 'div') as React.ElementType;

  return (
    <Component
      data-slot="card"
      className={cn(
        'tov:flex tov:flex-col tov:gap-6 tov:rounded-xl tov:border tov:bg-card tov:py-6 tov:text-card-foreground tov:shadow-sm',
        className,
      )}
      {...(props as React.ComponentPropsWithoutRef<TElement>)}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'tov:@container/card-header tov:grid tov:auto-rows-min tov:grid-rows-[auto_auto] tov:items-start tov:gap-2 tov:px-6 tov:has-data-[slot=card-action]:grid-cols-[1fr_auto] tov:[.border-b]:pb-6',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('tov:leading-none tov:font-semibold', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('tov:text-sm tov:text-muted-foreground', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'tov:col-start-2 tov:row-span-2 tov:row-start-1 tov:self-start tov:justify-self-end',
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('tov:px-6', className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'tov:flex tov:items-center tov:px-6 tov:[.border-t]:pt-6',
        className,
      )}
      {...props}
    />
  );
}

export type TovCardProps<TElement extends React.ElementType = 'section'> = Omit<
  React.ComponentPropsWithoutRef<TElement>,
  'title'
> & {
  as?: TElement;
  variant?: 'default' | 'content';
  hasBackground?: boolean;
  hasBorder?: boolean;
  isWide?: boolean;
  hasPadding?: boolean;
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  children?: React.ReactNode;
};

function TovCard<TElement extends React.ElementType = 'section'>({
  as,
  className,
  variant = 'default',
  hasBackground = false,
  hasBorder = false,
  isWide = false,
  hasPadding = false,
  eyebrow,
  title,
  children,
  ...props
}: TovCardProps<TElement>) {
  const Component = (as ?? 'section') as React.ElementType;
  const isContentVariant = variant === 'content';
  const hasContentCardAttributes =
    isContentVariant || hasBackground || hasBorder || isWide || hasPadding;

  return (
    <Component
      data-tov-card={hasContentCardAttributes ? '' : undefined}
      data-tov-card-background={hasBackground ? '' : undefined}
      data-tov-card-border={hasBorder ? '' : undefined}
      data-tov-card-wide={isWide ? '' : undefined}
      data-tov-card-padding={hasPadding ? '' : undefined}
      className={cn(isContentVariant ? 'content-card' : 'tov-card', className)}
      {...(props as React.ComponentPropsWithoutRef<TElement>)}
    >
      {eyebrow ? <p className="page-eyebrow">{eyebrow}</p> : null}
      {title ? <h3>{title}</h3> : null}
      {children}
    </Component>
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  TovCard,
};
